import { Tooltip } from '@ibsys/kit';
import { Pause } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationTranscript, PHRASE_TYPES } from '@/entities/conversations';
import { useTimeDisplayFormat } from '@/entities/settings';
import { formatTimeInterval, withSecondsLabel } from '@/shared/lib/utils/string';

type Props = {
  transcriptData: ConversationTranscript[];
  audioDuration: number;
};

type PauseInterval = {
  start: number; // в миллисекундах
  end: number; // в миллисекундах
  personId?: number | null; // ID участника, который сделал паузу
};

/**
 * Компонент для отображения интервалов пауз поверх визуализатора аудио
 * Отображается в более второстепенном стиле по сравнению с прерываниями
 */
export const PauseIntervals: FC<Props> = ({ transcriptData, audioDuration }) => {
  const { t } = useTranslation(['common', 'dialogs']);
  // Границы паузы — интервалы времени: показываются в действующем формате системы
  const timeDisplayFormat = useTimeDisplayFormat();
  const timeLabel = withSecondsLabel(t('dialogs:timeLabel'), timeDisplayFormat, t('common:secondsSuffix'));

  // Вычисляем общую длительность для позиционирования интервалов
  const totalDurationMs = audioDuration * 1000;

  // Извлекаем интервалы пауз из transcription
  const pauseIntervals = useMemo<PauseInterval[]>(() => {
    if (!transcriptData.length) {
      return [];
    }

    return transcriptData
      .filter((message) => message.type === PHRASE_TYPES.PAUSE)
      .filter((message) => message.startedAt != null && message.finishedAt != null)
      .map((message) => ({
        start: message.startedAt * 1000,
        end: message.finishedAt! * 1000,
        personId: message.personId,
      }))
      .filter((interval) => interval.start < interval.end);
  }, [transcriptData]);

  if (!totalDurationMs || pauseIntervals.length === 0) {
    return null;
  }

  return (
    <div className="absolute inset-0 pointer-events-none z-0">
      {pauseIntervals.map((interval, index) => {
        const startPercent = (interval.start / totalDurationMs) * 100;
        const widthPercent = ((interval.end - interval.start) / totalDurationMs) * 100;
        const startSeconds = interval.start / 1000;
        const endSeconds = interval.end / 1000;
        const startTime = formatTimeInterval(startSeconds, timeDisplayFormat);
        const endTime = formatTimeInterval(endSeconds, timeDisplayFormat);

        return (
          <div
            key={index}
            className="absolute top-0 bottom-0 bg-muted/20 border-l border-r border-muted/40 rounded-md"
            data-interval-sec={`${interval.start}-${interval.end}`}
            style={{
              left: `${startPercent}%`,
              width: `${widthPercent}%`,
            }}
          >
            {/* Кружок с иконкой паузы сверху интервала */}
            <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 pointer-events-auto">
              <Tooltip
                tooltip={
                  <div className="text-sm space-y-1 max-w-xs">
                    <div className="font-medium mb-1 text-xs">{t('dialogs:pause')}</div>
                    <div className="text-muted-foreground text-xs">
                      {t('dialogs:pauseTime', {
                        label: timeLabel,
                        start: startTime,
                        end: endTime,
                      })}
                    </div>
                  </div>
                }
              >
                <div className="flex items-center justify-center size-4 rounded-full bg-muted/60 text-muted-foreground border border-border/50 shadow-sm">
                  <Pause className="size-2.5" />
                </div>
              </Tooltip>
            </div>
          </div>
        );
      })}
    </div>
  );
};
